import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TemplateCopyService from "../../../services/contract/templateCopyService";

// Temporarily hidden: the New Contract -> Copy chooser. Copy is available from the Actions column of the contract list.
export const ENABLE_NEW_CONTRACT_COPY_MODAL = false;

// Where a freshly created / copied contract is opened, by contract type
const CREATE_ROUTES = {
  Lease: "/create-lease",
  Loan: "/create-loan",
  Rental: "/create-rental",
  "Line of Credit": "/create-line-of-credit",
};
const EDIT_ROUTES = {
  Lease: "/contract/edit",
  Loan: "/editloancontract",
  Rental: "/editrentalcontract",
  "Line of Credit": "/edit-line-of-credit-contract",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const boxStyle = {
  background: "#fff",
  borderRadius: 8,
  width: 560,
  maxWidth: "95vw",
  maxHeight: "90vh",
  overflowY: "auto",
  padding: 24,
};

// Step 1: Create New / Copy -> Step 2: Template / Contract -> Step 3: search by
// description or contract number, pick the source and copy it.
function NewContractModal({ contractType, onClose }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("new"); // new | copy
  const [source, setSource] = useState("template"); // template | contract
  const [searchBy, setSearchBy] = useState("description"); // description | number
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    setSelected(null);
    setResults([]);
  }, [mode, source, searchBy]);

  useEffect(() => {
    if (mode !== "copy") return undefined;
    // small debounce so we don't hit the API on every keystroke
    const timer = setTimeout(() => {
      setSearching(true);
      TemplateCopyService.searchCopySource(source, contractType, query)
        .then((response) => setResults(response.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [mode, source, query, contractType]);

  const visibleResults = results.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return searchBy === "number"
      ? String(r.contract_number || "").toLowerCase().includes(q)
      : String(r.description || "").toLowerCase().includes(q);
  });

  const handleContinue = () => {
    if (mode === "new") {
      navigate(CREATE_ROUTES[contractType], { state: contractType });
      onClose();
      return;
    }
    if (!selected) {
      toast.error(`Select a ${source} to copy`);
      return;
    }
    setCopying(true);
    TemplateCopyService.copyContract(selected.id, contractType)
      .then((response) => {
        if (response.data.success === 1) {
          toast.success(`Contract ${response.data.contractNo} created from ${selected.contract_number}`);
          navigate(EDIT_ROUTES[contractType], { state: response.data.contractID });
          onClose();
        } else {
          toast.error(response.data.error || "Unable to copy contract");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Unable to copy contract");
      })
      .finally(() => setCopying(false));
  };

  const radio = (name, value, current, setter, label) => (
    <div className="form-check form-check-inline">
      <input
        className="form-check-input"
        type="radio"
        name={name}
        id={`${name}_${value}`}
        checked={current === value}
        onChange={() => setter(value)}
      />
      <label className="form-check-label" htmlFor={`${name}_${value}`}>
        {label}
      </label>
    </div>
  );

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <h5 className="mb-3">New {contractType} Contract</h5>

        <div className="mb-3">
          <label className="col-form-label d-block">
            <b>How do you want to start?</b>
          </label>
          {radio("ncm_mode", "new", mode, setMode, "Create New")}
          {radio("ncm_mode", "copy", mode, setMode, "Copy")}
        </div>

        {mode === "copy" && (
          <>
            <div className="mb-3">
              <label className="col-form-label d-block">
                <b>Copy from</b>
              </label>
              {radio("ncm_source", "template", source, setSource, "Template")}
              {radio("ncm_source", "contract", source, setSource, "Contract")}
            </div>

            <div className="mb-3">
              <label className="col-form-label d-block">
                <b>Find by</b>
              </label>
              {radio("ncm_search", "description", searchBy, setSearchBy, "Description")}
              {radio("ncm_search", "number", searchBy, setSearchBy, "Contract Number")}
              <input
                type="text"
                className="form-control mt-2"
                placeholder={
                  searchBy === "number" ? "Enter contract number" : "Enter description"
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div
              className="border rounded mb-3"
              style={{ maxHeight: 220, overflowY: "auto" }}
            >
              {searching ? (
                <div className="p-3 text-muted">Searching...</div>
              ) : visibleResults.length === 0 ? (
                <div className="p-3 text-muted">
                  No {source === "template" ? "templates" : "contracts"} found
                </div>
              ) : (
                visibleResults.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="p-2 border-bottom"
                    style={{
                      cursor: "pointer",
                      background: selected && selected.id === r.id ? "#e7f3fa" : "#fff",
                    }}
                  >
                    <b>{r.contract_number}</b>
                    {r.customer_name ? ` - ${r.customer_name}` : ""}
                    <div className="small text-muted">{r.description || "No description"}</div>
                  </div>
                ))
              )}
            </div>
            <p className="small text-muted">
              Contract details, assets, payments and terms &amp; conditions are copied.
              Payment details, amortization and additional parties are not. You can update
              any value before activation.
            </p>
          </>
        )}

        <div className="d-flex justify-content-end" style={{ gap: 8 }}>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={copying || (mode === "copy" && !selected)}
            onClick={handleContinue}
          >
            {mode === "new" ? "Continue" : copying ? "Copying..." : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewContractModal;
